import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/Dialog';
import { Button } from '@/shared/components/ui/Button';
import { useCreateTask } from '@/shared/hooks';
import type { RelatedSignalsApiResponse } from '@momentum/types';
import {
  defaultValues,
  type CreateTaskFormData,
} from '@/features/goals/goal-form/types/tasks.types';
import { TaskForm } from '@/features/goals/goal-form/TaskForm';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';

interface CreateTaskModalForGoalProps {
  parentGoalId: string;
  trigger?: React.ReactNode;
}

export const CreateTaskModalForGoal: React.FC<CreateTaskModalForGoalProps> = ({
  parentGoalId,
  trigger,
}) => {
  const { t } = useTranslation('goals');
  const [open, setOpen] = useState(false);
  const createTaskMutation = useCreateTask();
  const currentEmployee = useEmployeeContext();

  const initialValues = useMemo<CreateTaskFormData>(() => {
    return {
      ...defaultValues,
      parent: parentGoalId,
      assignees: [currentEmployee._id],
      viewers: {
        users: [currentEmployee._id],
        teams: [],
      },
    };
  }, [parentGoalId, currentEmployee._id]);

  const handleSubmit = async (
    formData: CreateTaskFormData,
    signals: RelatedSignalsApiResponse[],
    hasReceivedSignalSuggestions?: boolean
  ) => {
    const { description, ...taskData } = formData;
    await createTaskMutation.mutateAsync({
      task: {
        ...taskData,
        ...(description?.trim() ? { description } : {}),
        assignees: taskData.assignees ?? [],
        parent: parentGoalId,
        relatedSignals: signals.map((signal) => ({
          signalId: signal.id,
          addedAt: new Date().toISOString(),
          addedBy: currentEmployee._id,
          ...(signal.contextualTitle
            ? { contextualTitle: signal.contextualTitle }
            : {}),
        })),
      },
      hasReceivedSignalSuggestions,
    });

    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const defaultTrigger = (
    <Button
      variant="outline"
      size="sm"
      className="h-8 gap-1.5 px-2.5 text-xs font-medium"
    >
      <Plus className="h-3.5 w-3.5" />
      {t('createTaskButton')}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger ? (
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : (
        <DialogTrigger asChild>{defaultTrigger}</DialogTrigger>
      )}
      <DialogContent size="lg" className="flex flex-col max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="text-xl font-bold">
            {t('createTaskTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 px-6 py-4">
          <TaskForm
            initialValues={initialValues}
            isPending={createTaskMutation.isPending}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel={t('createTask')}
            submittingLabel={t('creating')}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
